import { defineStore } from 'pinia'
import { ref, computed, watch, reactive } from 'vue'
import { logger } from '@/lib/logger'
import { openDB, type IDBPDatabase } from 'idb'

interface CacheEntry {
  url: string
  blob: Blob
  objectUrl: string
  timestamp: number
  accessCount: number
  lastAccessed: number
  signedUrlExpiresAt: number // Track when Supabase signed URL expires
}

interface IDBCacheEntry {
  url: string // Primary key
  blob: Blob
  timestamp: number
  accessCount: number
  lastAccessed: number
  blobSize: number
}

interface CacheStats {
  size: number
  hitRate: number
  totalRequests: number
  cacheHits: number
  cacheMisses: number
}

/**
 * Centralized Image Cache Store with IndexedDB
 * 
 * Features:
 * - Cross-tab cache sharing via IndexedDB
 * - Persistent cache across browser sessions
 * - In-memory objectURL cache for fast lookups
 * - Automatic signed URL expiration handling (Supabase URLs expire in 1 hour)
 * - LRU eviction when cache limit reached
 * - 400MB cache limit (up from 50MB)
 */
export const useImageCacheStore = defineStore('imageCache', () => {
  // In-memory cache for fast objectURL lookups (per-tab)
  const inMemoryCache = ref(new Map<string, CacheEntry>())
  const inFlightRequests = new Map<string, Promise<string>>()
  
  // Track failed paths with exponential backoff to prevent infinite retry loops
  // Map<cacheKey, { failCount: number, nextRetryAt: number }>
  const failedPaths = new Map<string, { failCount: number; nextRetryAt: number }>()
  const MAX_FAIL_COUNT = 5 // Stop retrying after 5 failures
  const BASE_BACKOFF_MS = 5000 // 5 seconds initial backoff
  
  // IndexedDB instance (shared across tabs, persistent)
  let db: IDBPDatabase | null = null
  let dbInitPromise: Promise<void> | null = null // Deduplication for init
  const DB_NAME = 'ambi-image-cache'
  const DB_VERSION = 1
  const STORE_NAME = 'images'
  
  // Configuration - Large cache for desktop apps with many inherited images
  const maxCacheSize = 1024 * 1024 * 1024 // 1GB cache limit
  const maxInMemoryItems = 500 // Increase in-memory cache for faster lookups
  const maxCacheAge = 24 * 60 * 60 * 1000 // 24 hours for blobs
  const signedUrlTTL = 55 * 60 * 1000 // 55 minutes (Supabase URLs expire at 60 min, refresh at 55)
  let currentCacheSize = 0
  
  // Version counter for reactivity - increment when cache is updated
  // Components can watch this to know when to re-render
  const cacheVersion = ref(0)
  
  // Statistics
  const stats = ref({
    totalRequests: 0,
    cacheHits: 0,
    cacheMisses: 0
  })
  
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  /**
   * Initialize IndexedDB - with deduplication to prevent multiple parallel inits
   */
  const initDB = async (): Promise<void> => {
    if (db) return // Already initialized
    
    // Deduplicate concurrent calls - return existing promise if init in progress
    if (dbInitPromise) return dbInitPromise

    dbInitPromise = (async () => {
      try {
        db = await openDB(DB_NAME, DB_VERSION, {
          upgrade(db) {
            if (!db.objectStoreNames.contains(STORE_NAME)) {
              const store = db.createObjectStore(STORE_NAME, { keyPath: 'url' })
              store.createIndex('timestamp', 'timestamp')
              store.createIndex('lastAccessed', 'lastAccessed')
              logger.info('📦 [imageCache] Created IndexedDB object store')
            }
          }
        })

        // Calculate current cache size from IndexedDB
        await recalculateCacheSize()
        
        // Hydrate in-memory cache from IndexedDB for instant access
        await hydrateInMemoryCache()
        
        logger.info('✅ [imageCache] IndexedDB initialized', {
          dbName: DB_NAME,
          version: DB_VERSION,
          currentSize: currentCacheSize,
          inMemoryItems: inMemoryCache.value.size
        })
      } catch (err) {
        logger.error('❌ [imageCache] Failed to initialize IndexedDB:', err)
        error.value = 'Failed to initialize cache database'
        dbInitPromise = null // Allow retry on failure
      }
    })()
    
    return dbInitPromise
  }

  /**
   * Recalculate total cache size from IndexedDB
   */
  const recalculateCacheSize = async (): Promise<void> => {
    if (!db) return

    try {
      const tx = db.transaction(STORE_NAME, 'readonly')
      const store = tx.objectStore(STORE_NAME)
      const allEntries = await store.getAll()
      
      currentCacheSize = allEntries.reduce((total, entry) => total + (entry.blobSize || 0), 0)
      
      logger.debug('📊 [imageCache] Recalculated cache size:', {
        entries: allEntries.length,
        sizeBytes: currentCacheSize,
        sizeMB: (currentCacheSize / (1024 * 1024)).toFixed(2)
      })
    } catch (err) {
      logger.error('❌ [imageCache] Failed to recalculate cache size:', err)
    }
  }

  /**
   * Hydrate in-memory cache from IndexedDB for instant access on page load
   * This allows getCachedUrlByPath to return URLs immediately without waiting for explicit preloads
   */
  const hydrateInMemoryCache = async (): Promise<void> => {
    if (!db) return

    try {
      const tx = db.transaction(STORE_NAME, 'readonly')
      const store = tx.objectStore(STORE_NAME)
      const allEntries: IDBCacheEntry[] = await store.getAll()
      
      const now = Date.now()
      let hydratedCount = 0
      let skippedCount = 0
      
      for (const dbEntry of allEntries) {
        // Skip if already in memory (shouldn't happen on fresh load, but just in case)
        if (inMemoryCache.value.has(dbEntry.url)) {
          continue
        }
        
        // Skip expired entries
        const blobAge = now - dbEntry.timestamp
        if (blobAge >= maxCacheAge) {
          skippedCount++
          continue
        }
        
        // Check in-memory limit - stop if we've reached it
        if (inMemoryCache.value.size >= maxInMemoryItems) {
          logger.debug('📦 [imageCache] Reached in-memory limit during hydration, stopping')
          break
        }
        
        try {
          // Validate blob before creating object URL
          // In WebKit-based WebViews (Tauri), blobs from IndexedDB can become invalid
          if (!dbEntry.blob || dbEntry.blob.size === 0) {
            logger.warn('⚠️ [imageCache] Invalid blob during hydration, skipping:', { url: dbEntry.url })
            skippedCount++
            continue
          }
          
          const objectUrl = URL.createObjectURL(dbEntry.blob)
          const entry: CacheEntry = {
            url: dbEntry.url,
            blob: dbEntry.blob,
            objectUrl,
            timestamp: dbEntry.timestamp,
            accessCount: dbEntry.accessCount,
            lastAccessed: dbEntry.lastAccessed,
            signedUrlExpiresAt: now + signedUrlTTL // Set future expiration for hydrated entries
          }
          
          inMemoryCache.value.set(dbEntry.url, entry)
          hydratedCount++
        } catch (err) {
          logger.warn('⚠️ [imageCache] Failed to hydrate entry:', { url: dbEntry.url, err })
        }
      }
      
      if (hydratedCount > 0) {
        cacheVersion.value++ // Trigger reactivity for components watching cache
        logger.info('💧 [imageCache] Hydrated in-memory cache from IndexedDB:', {
          hydrated: hydratedCount,
          skipped: skippedCount,
          total: allEntries.length
        })
      }
    } catch (err) {
      logger.error('❌ [imageCache] Failed to hydrate in-memory cache:', err)
    }
  }

  /**
   * Get cache statistics
   */
  const getCacheStats = computed((): CacheStats => {
    const size = currentCacheSize
    const totalRequests = stats.value.totalRequests
    const cacheHits = stats.value.cacheHits
    const cacheMisses = stats.value.cacheMisses
    const hitRate = totalRequests > 0 ? (cacheHits / totalRequests) * 100 : 0

    return {
      size,
      hitRate,
      totalRequests,
      cacheHits,
      cacheMisses
    }
  })

  /**
   * Get current cache size in MB
   */
  const getCacheSizeMB = computed(() => {
    return (currentCacheSize / (1024 * 1024)).toFixed(2)
  })

  /**
   * Get number of cached images
   */
  const getCacheCount = computed(() => {
    return inMemoryCache.value.size
  })

  /**
   * Check if signed URL has expired or will expire soon
   */
  const isSignedUrlExpired = (expiresAt: number): boolean => {
    return Date.now() >= expiresAt
  }

  /**
   * Extract expiration time from Supabase signed URL
   */
  const extractExpirationFromUrl = (url: string): number => {
    try {
      const urlObj = new URL(url)
      const tokenParam = urlObj.searchParams.get('token')
      if (!tokenParam) {
        // Default to 55 minutes from now if we can't parse
        return Date.now() + signedUrlTTL
      }
      
      // Try to decode the JWT token to get the actual expiration
      try {
        // The token is usually a JWT (header.payload.signature)
        // We want the payload (middle part)
        const parts = tokenParam.split('.')
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]))
          if (payload.exp) {
            // Convert unix timestamp (seconds) to milliseconds
            // Subtract 5 minutes buffer to refresh before actual expiry
            return (payload.exp * 1000) - (5 * 60 * 1000)
          }
        }
      } catch (e) {
        // If JWT parsing fails, fall back to default TTL
      }
      
      // Fallback: Supabase tokens are typically valid for 1 hour
      // We'll set expiration to 55 minutes to refresh before actual expiry
      return Date.now() + signedUrlTTL
    } catch {
      return Date.now() + signedUrlTTL
    }
  }

  /**
   * Evict least recently used entries from IndexedDB to make room
   */
  const evictLRUFromDB = async (requiredSpace: number): Promise<void> => {
    if (!db) return

    try {
      const tx = db.transaction(STORE_NAME, 'readwrite')
      const store = tx.objectStore(STORE_NAME)
      const index = store.index('lastAccessed')
      
      // Get all entries sorted by lastAccessed (oldest first)
      const allEntries = await index.getAll()
      
      for (const entry of allEntries) {
        if (currentCacheSize + requiredSpace <= maxCacheSize) {
          break
        }

        await store.delete(entry.url)
        currentCacheSize -= entry.blobSize
        
        // Also remove from in-memory cache if present
        const memEntry = inMemoryCache.value.get(entry.url)
        if (memEntry) {
          URL.revokeObjectURL(memEntry.objectUrl)
          inMemoryCache.value.delete(entry.url)
        }
        
        logger.debug('🗑️ [imageCache] Evicted LRU entry from IndexedDB:', {
          url: entry.url,
          size: entry.blobSize
        })
      }

      await tx.done
    } catch (err) {
      logger.error('❌ [imageCache] Failed to evict LRU entries:', err)
    }
  }

  /**
   * Evict least recently used entries from in-memory cache to stay under item limit
   * This prevents memory pressure on mobile devices like iOS Safari
   */
  const evictLRUFromMemory = (): void => {
    if (inMemoryCache.value.size <= maxInMemoryItems) return
    
    // Get all entries sorted by lastAccessed (oldest first)
    const entries = Array.from(inMemoryCache.value.entries())
      .sort(([, a], [, b]) => a.lastAccessed - b.lastAccessed)
    
    // Calculate how many to evict (remove 20% of limit to avoid frequent evictions)
    const evictCount = Math.max(1, Math.ceil(maxInMemoryItems * 0.2))
    const toEvict = entries.slice(0, evictCount)
    
    for (const [key, entry] of toEvict) {
      URL.revokeObjectURL(entry.objectUrl)
      inMemoryCache.value.delete(key)
    }
    
    logger.debug('🗑️ [imageCache] Evicted LRU entries from in-memory cache:', {
      evicted: toEvict.length,
      remaining: inMemoryCache.value.size
    })
  }

  /**
   * Remove expired entries from IndexedDB
   */
  const cleanupExpired = async (): Promise<void> => {
    if (!db) return

    const now = Date.now()
    
    try {
      const tx = db.transaction(STORE_NAME, 'readwrite')
      const store = tx.objectStore(STORE_NAME)
      const index = store.index('timestamp')
      
      const allEntries = await index.getAll()
      const expiredKeys: string[] = []

      for (const entry of allEntries) {
        if (now - entry.timestamp > maxCacheAge) {
          expiredKeys.push(entry.url)
          await store.delete(entry.url)
          currentCacheSize -= entry.blobSize
          
          // Also remove from in-memory cache
          const memEntry = inMemoryCache.value.get(entry.url)
          if (memEntry) {
            URL.revokeObjectURL(memEntry.objectUrl)
            inMemoryCache.value.delete(entry.url)
          }
        }
      }

      await tx.done

      if (expiredKeys.length > 0) {
        logger.info(`🧹 [imageCache] Cleaned up ${expiredKeys.length} expired entries from IndexedDB`)
      }
    } catch (err) {
      logger.error('❌ [imageCache] Failed to cleanup expired entries:', err)
    }
  }

  /**
   * Extract a stable cache key from the URL
   * Removes query parameters (like signed tokens) to ensure cache hits
   */
  const getCacheKey = (url: string): string => {
    try {
      const urlObj = new URL(url)
      // Use the pathname as the stable key
      // e.g. /storage/v1/object/sign/user-images/uuid/timestamp-name.png
      return urlObj.pathname
    } catch {
      // Fallback to full URL if parsing fails
      return url
    }
  }

  /**
   * Extract storage path from a signed URL or return as-is if already a path
   * e.g. /storage/v1/object/sign/user-images/uuid/file.jpg → user-images/uuid/file.jpg
   */
  const getStoragePathFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url)
      const match = urlObj.pathname.match(/\/storage\/v1\/object\/sign\/(.+?)$/)
      if (match) {
        return match[1]
      }
      // If it's already a path like "user-images/uuid/file.jpg", return as-is
      return url
    } catch {
      // Already a storage path, not a URL
      return url
    }
  }

  /**
   * Convert storage path to a cache key format
   * e.g. user-images/uuid/file.jpg → /storage/v1/object/sign/user-images/uuid/file.jpg
   */
  const storagPathToCacheKey = (storagePath: string): string => {
    // Normalize: if it's already a full path, extract just the storage part
    if (storagePath.startsWith('/storage/')) {
      return storagePath.split('?')[0] // Remove query params if present
    }
    return `/storage/v1/object/sign/${storagePath}`
  }

  /**
   * Check if a path has failed too many times and is in backoff period
   */
  const isInFailedBackoff = (cacheKey: string): boolean => {
    const failed = failedPaths.get(cacheKey)
    if (!failed) return false
    
    // If we've exceeded max retries, block forever (until cache clear)
    if (failed.failCount >= MAX_FAIL_COUNT) {
      return true
    }
    
    // If we're still in backoff period, don't retry
    if (Date.now() < failed.nextRetryAt) {
      return true
    }
    
    return false
  }

  /**
   * Record a failed fetch with exponential backoff
   */
  const recordFailure = (cacheKey: string): void => {
    const existing = failedPaths.get(cacheKey)
    const failCount = (existing?.failCount || 0) + 1
    // Exponential backoff: 5s, 10s, 20s, 40s, 80s...
    const backoffMs = BASE_BACKOFF_MS * Math.pow(2, failCount - 1)
    const nextRetryAt = Date.now() + backoffMs
    
    failedPaths.set(cacheKey, { failCount, nextRetryAt })
    
    logger.warn(`⏸️ [imageCache] Recorded failure #${failCount} for ${cacheKey}, next retry in ${backoffMs/1000}s`)
  }

  /**
   * Clear failure record for a path (call on successful fetch)
   */
  const clearFailure = (cacheKey: string): void => {
    if (failedPaths.has(cacheKey)) {
      failedPaths.delete(cacheKey)
      logger.debug(`✅ [imageCache] Cleared failure record for ${cacheKey}`)
    }
  }

  /**
   * Cache a blob directly by storage path - used after upload to make images
   * instantly available without a round-trip to storage for a signed URL.
   * 
   * @param storagePath - The storage path (e.g., "uuid/file.jpg")
   * @param blob - The image blob to cache
   */
  const cacheBlobDirectly = async (storagePath: string, blob: Blob): Promise<string> => {
    if (!storagePath || !blob) return ''

    const cacheKey = storagPathToCacheKey(storagePath)

    // Already cached? Return existing URL
    const existing = inMemoryCache.value.get(cacheKey)
    if (existing?.objectUrl) {
      return existing.objectUrl
    }

    await initDB()

    const objectUrl = URL.createObjectURL(blob)
    const now = Date.now()
    const blobSize = blob.size

    const entry: CacheEntry = {
      url: cacheKey,
      blob,
      objectUrl,
      timestamp: now,
      accessCount: 1,
      lastAccessed: now,
      signedUrlExpiresAt: now + signedUrlTTL
    }

    inMemoryCache.value.set(cacheKey, entry)
    cacheVersion.value++
    evictLRUFromMemory()

    // Persist to IndexedDB
    if (db) {
      try {
        if (currentCacheSize + blobSize > maxCacheSize) {
          await evictLRUFromDB(blobSize)
        }

        const dbEntry: IDBCacheEntry = {
          url: cacheKey,
          blob,
          timestamp: now,
          accessCount: 1,
          lastAccessed: now,
          blobSize
        }
        await db.put(STORE_NAME, dbEntry)
        currentCacheSize += blobSize

        logger.info('💾 [imageCache] Cached blob directly:', {
          storagePath,
          cacheKey,
          size: blobSize
        })
      } catch (err) {
        logger.error('❌ [imageCache] Failed to persist direct blob cache:', err)
      }
    }

    // Clear any previous failure record
    clearFailure(cacheKey)

    return objectUrl
  }

  /**
   * Get image by storage path - OPTIMIZED VERSION
   * Only requests a signed URL if the image is not already cached
   * This significantly reduces API calls to Supabase
   * 
   * @param storagePath - The storage path (e.g., "user-images/uuid/file.jpg")
   * @param bucket - The storage bucket (default: "user-images")
   */
  const getImageByPath = async (storagePath: string, bucket: string = 'user-images'): Promise<string> => {
    if (!storagePath) return ''

    const cacheKey = storagPathToCacheKey(storagePath)
    stats.value.totalRequests++

    // Cleanup expired entries periodically (10% chance)
    if (Math.random() < 0.1) {
      cleanupExpired()
    }

    // 0. Check if this path has failed too many times - PREVENT INFINITE RETRY
    if (isInFailedBackoff(cacheKey)) {
      const failed = failedPaths.get(cacheKey)
      logger.debug(`🚫 [imageCache] Skipping failed path (${failed?.failCount} failures):`, { storagePath, cacheKey })
      return ''
    }

    // 1. Check in-memory cache first (fastest)
    const cached = inMemoryCache.value.get(cacheKey)
    if (cached && !isSignedUrlExpired(cached.signedUrlExpiresAt)) {
      stats.value.cacheHits++
      cached.accessCount++
      cached.lastAccessed = Date.now()
      logger.debug('✅ [imageCache] In-memory cache hit (by path):', { storagePath, cacheKey })
      return cached.objectUrl
    }

    // 2. Check if already being fetched - MUST check BEFORE creating new promise
    let inFlight = inFlightRequests.get(cacheKey)
    if (inFlight) {
      logger.debug('⏳ [imageCache] Using in-flight request (by path):', { storagePath, cacheKey })
      return await inFlight
    }

    // 3. CRITICAL: Create and SET the promise BEFORE any async work
    // This prevents race conditions where multiple calls start before the first one sets the promise
    const processPromise = processImageByPath(storagePath, bucket, cacheKey)
    inFlightRequests.set(cacheKey, processPromise)
    
    return await processPromise
  }

  /**
   * Internal function to process image by path - handles DB lookup and network fetch
   */
  const processImageByPath = async (storagePath: string, bucket: string, cacheKey: string): Promise<string> => {
    await initDB()

    try {
      // Try IndexedDB (cross-tab, persistent)
      if (db) {
        try {
          const dbEntry = await db.get(STORE_NAME, cacheKey)
          if (dbEntry) {
            // For path-based requests, we don't have a signed URL to check expiration
            // The blob itself is still valid, only the signed URL would expire
            // So we can use the blob as long as it's not too old
            const blobAge = Date.now() - dbEntry.timestamp
            
            if (blobAge < maxCacheAge) {
              const objectUrl = URL.createObjectURL(dbEntry.blob)
              const entry: CacheEntry = {
                url: cacheKey,
                blob: dbEntry.blob,
                objectUrl,
                timestamp: dbEntry.timestamp,
                accessCount: dbEntry.accessCount + 1,
                lastAccessed: Date.now(),
                signedUrlExpiresAt: Date.now() + signedUrlTTL // Set future expiration
              }
              
              inMemoryCache.value.set(cacheKey, entry)
              cacheVersion.value++ // Trigger reactivity
              evictLRUFromMemory()
              
              // Update access stats in IndexedDB (fire and forget)
              db.put(STORE_NAME, {
                ...dbEntry,
                accessCount: entry.accessCount,
                lastAccessed: entry.lastAccessed
              }).catch(err => logger.warn('Failed to update stats', err))
              
              stats.value.cacheHits++
              logger.debug('✅ [imageCache] IndexedDB cache hit (by path):', { storagePath, cacheKey })
              return objectUrl
            } else {
              // Blob too old, remove it
              await db.delete(STORE_NAME, cacheKey)
              currentCacheSize -= dbEntry.blobSize
              }
            }
          } catch (err) {
            logger.error('❌ [imageCache] IndexedDB lookup error:', err)
          }
        }

        // 4. Cache miss - NOW we need a signed URL
        stats.value.cacheMisses++
        logger.debug('❌ [imageCache] Cache miss, requesting signed URL:', { storagePath, cacheKey })

        // Get signed URL from provider
        const { getDataProvider } = await import('@/lib/dataProvider')
        const provider = getDataProvider()
        const signedUrl = await provider.getFileUrl(bucket, storagePath, 3600)
        
        if (!signedUrl) {
          throw new Error(`Failed to get signed URL for: ${storagePath}`)
        }

        // Fetch the image
        const response = await fetch(signedUrl, {
          mode: 'cors',
          credentials: 'same-origin'
        })

        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.statusText}`)
        }

        const blob = await response.blob()
        const blobSize = blob.size

        // Evict if needed
        if (currentCacheSize + blobSize > maxCacheSize) {
          await evictLRUFromDB(blobSize)
        }

        const objectUrl = URL.createObjectURL(blob)
        const now = Date.now()
        const expiresAt = extractExpirationFromUrl(signedUrl)

        const entry: CacheEntry = {
          url: cacheKey,
          blob,
          objectUrl,
          timestamp: now,
          accessCount: 1,
          lastAccessed: now,
          signedUrlExpiresAt: expiresAt
        }

        inMemoryCache.value.set(cacheKey, entry)
        cacheVersion.value++ // Trigger reactivity
        evictLRUFromMemory()

        // Save to IndexedDB
        if (db) {
          try {
            const dbEntry: IDBCacheEntry = {
              url: cacheKey,
              blob,
              timestamp: now,
              accessCount: 1,
              lastAccessed: now,
              blobSize
            }
            
            await db.put(STORE_NAME, dbEntry)
            currentCacheSize += blobSize

            logger.info('💾 [imageCache] Cached image (by path):', {
              storagePath,
              cacheKey,
              size: blobSize,
              totalSize: currentCacheSize
            })
          } catch (err) {
            logger.error('❌ [imageCache] Failed to save to IndexedDB:', err)
          }
        }

        // Clear any previous failure record on success
        clearFailure(cacheKey)

        return objectUrl
      } catch (err) {
        logger.error('❌ [imageCache] Fetch error (by path):', err)
        // Record failure with exponential backoff to prevent infinite retry
        recordFailure(cacheKey)
        return ''
      } finally {
        inFlightRequests.delete(cacheKey)
      }
  }

  /**
   * Preload multiple images by storage path - OPTIMIZED VERSION
   * Only fetches images not already cached
   */
  const preloadImagesByPath = async (storagePaths: string[], bucket: string = 'user-images'): Promise<void> => {
    const uniquePaths = [...new Set(storagePaths)].filter(path => {
      if (!path) return false
      const cacheKey = storagPathToCacheKey(path)
      return !inMemoryCache.value.has(cacheKey) && !inFlightRequests.has(cacheKey)
    })
    
    if (uniquePaths.length === 0) {
      logger.debug('⏭️ [imageCache] All images already cached (by path)')
      return
    }

    logger.info('🔄 [imageCache] Preloading images by path:', {
      total: storagePaths.length,
      toPreload: uniquePaths.length
    })

    isLoading.value = true
    try {
      await Promise.allSettled(uniquePaths.map(path => getImageByPath(path, bucket)))
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Get cached URL by storage path synchronously
   * Returns the objectUrl if cached, empty string otherwise
   * Note: The blob URL never expires - only the signed URL does, and that's only needed for re-downloading
   * Note: Access cacheVersion.value to create a reactive dependency
   */
  const getCachedUrlByPath = (storagePath: string): string => {
    // Access cacheVersion to create reactive dependency - components will re-render when cache updates
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    cacheVersion.value
    
    const cacheKey = storagPathToCacheKey(storagePath)
    const entry = inMemoryCache.value.get(cacheKey)
    // Return the blob URL if it exists - the blob never expires, only the signed URL does
    if (entry?.objectUrl) {
      return entry.objectUrl
    }
    return ''
  }

  /**
   * Check if image is cached by storage path (has valid blob URL)
   */
  const isCachedByPath = (storagePath: string): boolean => {
    const cacheKey = storagPathToCacheKey(storagePath)
    const entry = inMemoryCache.value.get(cacheKey)
    return entry?.objectUrl !== undefined
  }

  /**
   * Invalidate (remove) a cached image by storage path
   * Used when a blob URL becomes invalid (e.g., in WebKit WebViews)
   */
  const invalidateByPath = async (storagePath: string): Promise<void> => {
    const cacheKey = storagPathToCacheKey(storagePath)
    const entry = inMemoryCache.value.get(cacheKey)
    
    if (entry) {
      // Revoke the blob URL to free memory
      if (entry.objectUrl) {
        URL.revokeObjectURL(entry.objectUrl)
      }
      // Remove from in-memory cache
      inMemoryCache.value.delete(cacheKey)
      cacheVersion.value++ // Trigger reactivity
      
      logger.info('🗑️ [imageCache] Invalidated cache entry:', { storagePath, cacheKey })
    }
    
    // Also remove from IndexedDB
    if (db) {
      try {
        await db.delete(STORE_NAME, cacheKey)
      } catch (err) {
        logger.warn('⚠️ [imageCache] Failed to delete from IndexedDB:', { cacheKey, err })
      }
    }
    
    // Clear any failure record so it can be re-fetched
    clearFailure(cacheKey)
  }

  /**
   * Get cached image or fetch and cache it
   * Deduplicates in-flight requests
   */
  const getImage = async (url: string): Promise<string> => {
    if (!url) return ''

    const cacheKey = getCacheKey(url)
    stats.value.totalRequests++

    // Cleanup expired entries periodically (10% chance)
    if (Math.random() < 0.1) {
      cleanupExpired()
    }

    // Check if this URL has failed too many times - PREVENT INFINITE RETRY
    if (isInFailedBackoff(cacheKey)) {
      const failed = failedPaths.get(cacheKey)
      logger.debug(`🚫 [imageCache] Skipping failed URL (${failed?.failCount} failures):`, { url, cacheKey })
      return ''
    }

    // Check in-memory cache first (fastest)
    const cached = inMemoryCache.value.get(cacheKey)
    if (cached) {
      // Check if signed URL has expired
      if (isSignedUrlExpired(cached.signedUrlExpiresAt)) {
        logger.debug('⏰ [imageCache] Signed URL expired, will refetch:', { url, cacheKey })
        // Remove from in-memory cache, will refetch below
        URL.revokeObjectURL(cached.objectUrl)
        inMemoryCache.value.delete(cacheKey)
      } else {
        stats.value.cacheHits++
        cached.accessCount++
        cached.lastAccessed = Date.now()
        logger.debug('✅ [imageCache] In-memory cache hit:', { url, cacheKey })
        return cached.objectUrl
      }
    }

    // Check if already being fetched (or checking DB)
    const inFlight = inFlightRequests.get(cacheKey)
    if (inFlight) {
      logger.debug('⏳ [imageCache] Using in-flight request:', { url, cacheKey })
      return await inFlight
    }

    // Create a unified promise for DB check + Network fetch
    const processPromise = (async () => {
      await initDB() // Ensure DB is initialized

      try {
        // 1. Try IndexedDB (cross-tab, persistent)
        if (db) {
          try {
            const dbEntry = await db.get(STORE_NAME, cacheKey)
            if (dbEntry) {
              // Found in IndexedDB, create objectURL and add to in-memory cache
              const objectUrl = URL.createObjectURL(dbEntry.blob)
              const expiresAt = extractExpirationFromUrl(url)
              
              if (isSignedUrlExpired(expiresAt)) {
                logger.debug('⏰ [imageCache] Signed URL in IndexedDB expired, will refetch:', { url, cacheKey })
                // Remove from IndexedDB
                await db.delete(STORE_NAME, cacheKey)
                currentCacheSize -= dbEntry.blobSize
              } else {
                // If the stored expiration is very old (e.g. from a previous session where we guessed),
                // update it with the new expiration from the current URL
                const currentUrlExpiration = extractExpirationFromUrl(url)
                const effectiveExpiration = Math.max(expiresAt, currentUrlExpiration)
                
                const entry: CacheEntry = {
                  url: cacheKey,
                  blob: dbEntry.blob,
                  objectUrl,
                  timestamp: dbEntry.timestamp,
                  accessCount: dbEntry.accessCount + 1,
                  lastAccessed: Date.now(),
                  signedUrlExpiresAt: effectiveExpiration
                }
                
                inMemoryCache.value.set(cacheKey, entry)
                cacheVersion.value++ // Trigger reactivity
                evictLRUFromMemory() // Enforce in-memory item limit
                
                // Update access stats in IndexedDB
                // Don't await this to keep it fast
                db.put(STORE_NAME, {
                  ...dbEntry,
                  accessCount: entry.accessCount,
                  lastAccessed: entry.lastAccessed
                }).catch(err => logger.warn('Failed to update stats', err))
                
                stats.value.cacheHits++
                logger.debug('✅ [imageCache] IndexedDB cache hit:', { url, cacheKey })
                return objectUrl
              }
            }
          } catch (err) {
            logger.error('❌ [imageCache] IndexedDB lookup error:', err)
          }
        }

        // 2. Cache miss - fetch the image
        stats.value.cacheMisses++
        logger.debug('❌ [imageCache] Cache miss, fetching:', { url, cacheKey })

        const response = await fetch(url, {
          mode: 'cors',
          credentials: 'same-origin'
        })

        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.statusText}`)
        }

        const blob = await response.blob()
        const blobSize = blob.size

        // Check if we need to evict entries
        if (currentCacheSize + blobSize > maxCacheSize) {
          await evictLRUFromDB(blobSize)
        }

        // Create object URL for the blob
        const objectUrl = URL.createObjectURL(blob)
        const now = Date.now()
        const expiresAt = extractExpirationFromUrl(url)

        const entry: CacheEntry = {
          url: cacheKey,
          blob,
          objectUrl,
          timestamp: now,
          accessCount: 1,
          lastAccessed: now,
          signedUrlExpiresAt: expiresAt
        }

        inMemoryCache.value.set(cacheKey, entry)
        cacheVersion.value++ // Trigger reactivity
        evictLRUFromMemory() // Enforce in-memory item limit

        // Save to IndexedDB
        if (db) {
          try {
            const dbEntry: IDBCacheEntry = {
              url: cacheKey,
              blob,
              timestamp: now,
              accessCount: 1,
              lastAccessed: now,
              blobSize
            }
            
            await db.put(STORE_NAME, dbEntry)
            currentCacheSize += blobSize

            logger.info('💾 [imageCache] Cached image in IndexedDB:', {
              url,
              cacheKey,
              size: blobSize,
              totalSize: currentCacheSize,
              expiresAt: new Date(expiresAt).toISOString()
            })
          } catch (err) {
            logger.error('❌ [imageCache] Failed to save to IndexedDB:', err)
          }
        }

        // Clear any previous failure record on success
        clearFailure(cacheKey)

        return objectUrl
      } catch (err) {
        logger.error('❌ [imageCache] Fetch error:', err)
        // Record failure with exponential backoff to prevent infinite retry
        recordFailure(cacheKey)
        return ''
      } finally {
        inFlightRequests.delete(cacheKey)
      }
    })()

    inFlightRequests.set(cacheKey, processPromise)
    return await processPromise
  }

  /**
   * Preload multiple images into cache
   */
  const preloadImages = async (urls: string[]): Promise<void> => {
    // Skip URLs that are already cached OR currently being fetched
    const uniqueUrls = [...new Set(urls)].filter(url => {
      const cacheKey = getCacheKey(url)
      return url && !inMemoryCache.value.has(cacheKey) && !inFlightRequests.has(cacheKey)
    })
    
    if (uniqueUrls.length === 0) {
      logger.debug('⏭️ [imageCache] All images already cached or loading')
      return
    }

    logger.info('🔄 [imageCache] Preloading images:', {
      totalRequested: urls.length,
      alreadyCached: urls.filter(url => inMemoryCache.value.has(url)).length,
      inFlight: urls.filter(url => inFlightRequests.has(url)).length,
      toPreload: uniqueUrls.length
    })

    isLoading.value = true
    error.value = null

    try {
      // Fetch all images in parallel
      const results = await Promise.allSettled(
        uniqueUrls.map(url => getImage(url))
      )

      const failed = results.filter(r => r.status === 'rejected').length
      if (failed > 0) {
        logger.warn(`⚠️ [imageCache] ${failed} images failed to preload`)
      }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Preload images for a specific scene
   */
  // Cache store references to avoid repeated dynamic imports
  let _layersStore: ReturnType<typeof import('./displayLayers').useDisplayLayersStore> | null = null
  let _configsStore: ReturnType<typeof import('./sceneDisplayConfigs').useSceneDisplayConfigsStore> | null = null
  
  const getStores = async () => {
    if (!_layersStore || !_configsStore) {
      const [{ useDisplayLayersStore }, { useSceneDisplayConfigsStore }] = await Promise.all([
        import('./displayLayers'),
        import('./sceneDisplayConfigs')
      ])
      _layersStore = useDisplayLayersStore()
      _configsStore = useSceneDisplayConfigsStore()
    }
    return { layersStore: _layersStore, configsStore: _configsStore }
  }
  
  const preloadForScene = async (sceneId: string): Promise<void> => {
    logger.info('🎬 [imageCache] Preloading images for scene:', { sceneId })
    
    try {
      const { layersStore, configsStore } = await getStores()
      
      // Get all display configs for this scene
      const configs = configsStore.getConfigsForScene(sceneId)
      
      const storagePaths: string[] = []
      
      // Collect all storage paths from image layers
      for (const config of configs) {
        const layers = layersStore.layers.get(config.id) || []
        const paths = layers
          .filter(layer => layer.type === 'image' && layer.storage_path)
          .map(layer => layer.storage_path!)
        storagePaths.push(...paths)
      }
      
      if (storagePaths.length > 0) {
        await preloadImagesByPath(storagePaths, 'user-images')
      }
    } catch (err) {
      logger.error('❌ [imageCache] Failed to preload scene images:', err)
    }
  }

  /**
   * Preload images for a specific display in a scene
   */
  const preloadForDisplay = async (sceneId: string, displayId: string): Promise<void> => {
    logger.info('📺 [imageCache] Preloading images for display:', { sceneId, displayId })
    
    try {
      const { layersStore, configsStore } = await getStores()
      
      // Get the specific config
      const config = configsStore.getConfigForSceneAndDisplay(sceneId, displayId)
      if (!config) return
      
      const layers = layersStore.layers.get(config.id) || []
      const storagePaths = layers
        .filter(layer => layer.type === 'image' && layer.storage_path)
        .map(layer => layer.storage_path!)
      
      if (storagePaths.length > 0) {
        await preloadImagesByPath(storagePaths, 'user-images')
      }
    } catch (err) {
      logger.error('❌ [imageCache] Failed to preload display images:', err)
    }
  }

  /**
   * Clear a specific image from cache
   */
  const clearImage = async (url: string): Promise<void> => {
    // Remove from in-memory cache
    const entry = inMemoryCache.value.get(url)
    if (entry) {
      URL.revokeObjectURL(entry.objectUrl)
      inMemoryCache.value.delete(url)
    }

    // Remove from IndexedDB
    if (db) {
      try {
        const dbEntry = await db.get(STORE_NAME, url)
        if (dbEntry) {
          await db.delete(STORE_NAME, url)
          currentCacheSize -= dbEntry.blobSize
          logger.info('🗑️ [imageCache] Cleared image from IndexedDB:', { url })
        }
      } catch (err) {
        logger.error('❌ [imageCache] Failed to clear image from IndexedDB:', err)
      }
    }
  }

  /**
   * Clear all cached images
   */
  const clearCache = async (): Promise<void> => {
    logger.info('🗑️ [imageCache] Clearing entire cache')
    
    // Clear in-memory cache
    for (const entry of inMemoryCache.value.values()) {
      URL.revokeObjectURL(entry.objectUrl)
    }
    inMemoryCache.value.clear()
    inFlightRequests.clear()

    // Clear IndexedDB
    if (db) {
      try {
        const tx = db.transaction(STORE_NAME, 'readwrite')
        await tx.objectStore(STORE_NAME).clear()
        await tx.done
        logger.info('✅ [imageCache] Cleared IndexedDB')
      } catch (err) {
        logger.error('❌ [imageCache] Failed to clear IndexedDB:', err)
      }
    }
    
    // Clear failed paths tracking
    failedPaths.clear()
    logger.info('✅ [imageCache] Cleared failed paths tracking')
    
    currentCacheSize = 0
    stats.value = {
      totalRequests: 0,
      cacheHits: 0,
      cacheMisses: 0
    }
  }

  /**
   * Clear all failed path records (allow retrying)
   */
  const clearFailedPaths = (): void => {
    const count = failedPaths.size
    failedPaths.clear()
    logger.info(`🔄 [imageCache] Cleared ${count} failed path records - retries now allowed`)
  }

  /**
   * Get count of failed paths
   */
  const getFailedPathsCount = (): number => {
    return failedPaths.size
  }

  /**
   * Check if an image is in cache (in-memory or IndexedDB)
   */
  const isCached = async (url: string): Promise<boolean> => {
    const cacheKey = getCacheKey(url)
    if (inMemoryCache.value.has(cacheKey)) return true
    
    if (db) {
      try {
        const entry = await db.get(STORE_NAME, cacheKey)
        return !!entry
      } catch {
        return false
      }
    }
    
    return false
  }

  /**
   * Get cached URL synchronously if available in memory, otherwise return original
   * Use this for immediate display without triggering fetches
   */
  const getCachedUrl = (url: string): string => {
    const cacheKey = getCacheKey(url)
    const entry = inMemoryCache.value.get(cacheKey)
    if (entry && !isSignedUrlExpired(entry.signedUrlExpiresAt)) {
      return entry.objectUrl
    }
    return url
  }

  /**
   * Setup smart preloading watchers
   * Called when store is initialized
   */
  const setupSmartPreloading = async () => {
    try {
      // Watch for active scene changes
      const { useScenesStore } = await import('./scenes')
      const scenesStore = useScenesStore()
      
      watch(
        () => scenesStore.getActiveScene,
        async (newScene) => {
          if (newScene?.id) {
            logger.info('🎯 [imageCache] Active scene changed, preloading:', { sceneId: newScene.id })
            await preloadForScene(newScene.id)
          }
        },
        { immediate: true }
      )
    } catch (err) {
      logger.error('❌ [imageCache] Failed to setup smart preloading:', err)
    }
  }

  // Initialize DB and smart preloading (don't await to avoid blocking store setup)
  initDB().then(() => {
    setupSmartPreloading().catch(err => {
      logger.error('❌ [imageCache] Failed to initialize smart preloading:', err)
    })
  })

  return {
    // State
    isLoading,
    error,
    
    // Computed
    getCacheStats,
    getCacheSizeMB,
    getCacheCount,
    
    // Reactivity helper - watch this to know when cache updates
    cacheVersion,
    
    // Methods - URL based (legacy)
    getImage,
    preloadImages,
    getCachedUrl,
    isCached,
    
    // Methods - Path based (optimized - use these!)
    getImageByPath,
    preloadImagesByPath,
    getCachedUrlByPath,
    isCachedByPath,
    invalidateByPath,
    cacheBlobDirectly,
    
    // Utilities
    getStoragePathFromUrl,
    storagPathToCacheKey,
    
    // Scene/Display preloading
    preloadForScene,
    preloadForDisplay,
    
    // Cache management
    clearImage,
    clearCache,
    clearFailedPaths,
    getFailedPathsCount,
    setupSmartPreloading
  }
})